import React from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import InfoBlock from "../../../../templates/info-block/infoBlock";
import InfoGrid from "../../../../templates/info-grid/infoGrid";
import LabeledValue from "../../../../ui-components/molecules/labeled-value/labeledValue";
import BlockPlaceholder from "../../../../ui-components/block-placeholder/blockPlaceholder";
import { InvoiceDetails } from "../../../../app/data/invoice/models";
import UserState from "../../../../slices/user/UserState";
import { userSelector } from "../../../../slices/user/userSlice";
import { UserUtils } from "../../../../app/data/user/userUtils";
import TrailerLocation from "../../../trailer-location/trailerLocation";
import { SHIPMENT_STATUSES } from "../../../../app/data/common/ShipmentStatuses";
import { AppointmentDto } from "../../../../app/data/appointments/models";
import "./invoiceDetailsInfo.scss";

export interface InvoiceDetailsInfoProps extends InvoiceDetails {
  appointmentRequired?: boolean;
  appointment?: AppointmentDto;
  showOnMap?: boolean;
  /** When true, only Payor / Shipper / Consignee blocks are rendered (same markup as shipment details). */
  partiesOnly?: boolean;
  /** When true with `partiesOnly`, the last two non-empty lines link to Google Maps (P.O. box-style address lines are plain text). */
  linkPartyAddressesToGoogleMaps?: boolean;
}

const partyInfoHasPoBoxLine = (text: string): boolean =>
  text.split(/\r?\n/).some((line) => {
    const n = line.trim().replace(/\s+/g, " ").toUpperCase();
    return n.startsWith("P O BOX") || n.startsWith("P.O. BOX") || n.startsWith("PO BOX");
  });

/** Name / attention above; the last two non-empty lines are the address used for the Google Maps query and link. */
const splitPartyInfoLastTwoLinesAsAddress = (info: string): { header: string; address: string | null } => {
  const lines = info.replace(/\r\n/g, "\n").split("\n");
  const nonemptyIdx: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().length > 0) nonemptyIdx.push(i);
  }
  if (nonemptyIdx.length < 2) {
    return { header: info, address: null };
  }
  const iFirst = nonemptyIdx[nonemptyIdx.length - 2]!;
  const iLast = nonemptyIdx[nonemptyIdx.length - 1]!;
  const address = lines.slice(iFirst, iLast + 1).join("\n");
  const header = lines.slice(0, iFirst).join("\n");
  return { header, address };
};

const PartyInfoBody: React.FC<{ info: string; linkifyToMaps: boolean }> = ({ info, linkifyToMaps }) => {
  if (!linkifyToMaps) {
    return <div className="common-text pre-wrap">{info}</div>;
  }

  const { header, address } = splitPartyInfoLastTwoLinesAsAddress(info);
  if (!address || partyInfoHasPoBoxLine(address)) {
    return <div className="common-text pre-wrap">{info}</div>;
  }

  const mapHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
  return (
    <div className="common-text pre-wrap">
      {header}
      {header ? "\n" : ""}
      <span className="xgs-invoice-details-party-maps__address">{address}</span>
      <a
        href={mapHref}
        target="_blank"
        rel="noopener noreferrer"
        className="xgs-invoice-details-party-maps__icon-link"
        aria-label="Open address in Google Maps"
      >
        <FontAwesomeIcon icon={Icons.faExternalLinkAlt} className="xgs-invoice-details-party-maps__icon" aria-hidden />
      </a>
    </div>
  );
};

const InvoiceDetailsInfo: React.FC<InvoiceDetailsInfoProps> = (props) => {
  const userState: UserState = useSelector(userSelector);

  const getContactPhoneComponent = (phone: string | undefined | null) => {    
    return (
      <span className="xgs-info-block__phone">
        <FontAwesomeIcon
          icon={phone ? Icons.faPhone : Icons.faPhoneSlash}
          size="lg"
          className="chart-icon"
        />
        {phone ? phone.formatPhone() : ""}
      </span>
    );
  }

  const isOutForDelivery = () => props.status?.description === SHIPMENT_STATUSES.outForDelivery;

  const partiesSection =
    (props.fullAccess !== false) && (
      <>
        <InfoBlock
          title={`Payor # ${props.payor?.number}`}
          headerInfo={getContactPhoneComponent(props.payor?.phone)}
          className="xgs-info-block--centered"
        >
          {props.payor?.info ? (
            <PartyInfoBody info={props.payor.info} linkifyToMaps={!!props.linkPartyAddressesToGoogleMaps} />
          ) : (
            <BlockPlaceholder />
          )}
        </InfoBlock>
        <InfoBlock
          title={`From: Shipper # ${props.shipper?.number}`}
          headerInfo={getContactPhoneComponent(props.shipper?.phone)}
          className="xgs-info-block--centered"
        >
          {props.shipper?.info ? (
            <PartyInfoBody info={props.shipper.info} linkifyToMaps={!!props.linkPartyAddressesToGoogleMaps} />
          ) : (
            <BlockPlaceholder />
          )}
        </InfoBlock>
        <InfoBlock
          title={`To: Consignee # ${props.consignee?.number}`}
          headerInfo={getContactPhoneComponent(props.consignee?.phone)}
          className="xgs-info-block--centered"
        >
          {props.consignee?.info ? (
            <PartyInfoBody info={props.consignee.info} linkifyToMaps={!!props.linkPartyAddressesToGoogleMaps} />
          ) : (
            <BlockPlaceholder />
          )}
        </InfoBlock>
      </>
    );

  if (props.partiesOnly) {
    return <InfoGrid className="xgs-invoice-details-grid">{partiesSection}</InfoGrid>;
  }

  return (
    <InfoGrid className="xgs-invoice-details-grid">
      {(props.fullAccess !== false) && (
        <>
          <InfoBlock title="Invoice Summary">
            <LabeledValue
              label="Invoice date:"
              value={props.invoice?.dateInvoice?.toUiDateFormat()}
              labelFixWidth
            />
            <LabeledValue
              label="Invoice number:"
              value={props.invoice?.invoiceNumber}
              labelFixWidth
            />
            <LabeledValue
              label="BOL:"
              value={props.invoice?.bolNumber}
              labelFixWidth
            />
            <LabeledValue
              label="PO number:"
              value={props.invoice.poNumber}
              labelFixWidth
              optional
            />
            <LabeledValue
              label="Type of bill:"
              value={props.typeOfBill}
              labelFixWidth
              optional
            />
          </InfoBlock>
        </>
      )}
      <InfoBlock
        title="XGS Origin Hub"
        headerInfo={getContactPhoneComponent(props.origin.phone)}
        className={(props.origin.name || props.origin.address || props.origin.city) ? "xgs-info-block--centered" : ""}
      >
        {(props.origin.name || props.origin.address || props.origin.city) && (
          <div>
            <div className="common-text">{`${props.origin.id ?? ""} ${props.origin.name ?? ""}`}</div>
            <div>&nbsp;</div>
            <div className="common-text">
              {props.origin.address ?? ""}<br />
              {`
                ${props.origin.city ? props.origin.city + ", " : ""} 
                ${props.origin.state ? props.origin.state + " " : ""} 
                ${props.origin.zip ?? ""}
              `}
            </div>
          </div>
        )}
        {!props.origin.address && !props.origin.city && <BlockPlaceholder />}
      </InfoBlock>
      <InfoBlock
        title="XGS Destination Hub"
        headerInfo={getContactPhoneComponent(props.destination.phone)}
        className={(props.destination.address || props.destination.city) ? "xgs-info-block--centered" : ""}
      >
        {(props.destination.name || props.destination.address || props.destination.city) && (
          <div>
            <div className="common-text">{`${props.destination.id ?? ""} ${props.destination.name ?? ""}`}</div>
            <div>&nbsp;</div>
            <div className="common-text">
              {props.destination.address ?? ""}<br />
              {`
                ${props.destination.city ? props.destination.city + ", " : ""} 
                ${props.destination.state ? props.destination.state + " " : ""} 
                ${props.destination.zip ?? ""}
              `}
            </div>
          </div>
        )}
        {!props.destination.name && !props.destination.address && !props.destination.city && <BlockPlaceholder />}
      </InfoBlock>
      {(props.fullAccess !== false) && (
        <>
          {partiesSection}
          {(props.status?.title !== "BOOKED") && (
            <InfoBlock title={UserUtils.isEmployee(userState.profile) ? "Trailer & Driver" : "Trailer & Manifest"}>
              {(props.trailer ||
                props.cheetahInfo?.driverName ||
                props.cheetahInfo?.driverNumber ||
                props.manifest?.linehaul ||
                props.manifest?.delivery ||
                isOutForDelivery()) && (
                <>
                  {props.trailer && <LabeledValue label="Trailer:" value={props.trailer} labelFixWidth />}
                  {UserUtils.isEmployee(userState.profile) && (props.cheetahInfo?.driverName || props.cheetahInfo?.driverNumber) && (
                    <LabeledValue label="Driver:" value={`${props.cheetahInfo?.driverName ? props.cheetahInfo.driverName : ""} ${props.cheetahInfo?.driverNumber ? props.cheetahInfo.driverNumber : ""}`} labelFixWidth />
                  )}
                  <LabeledValue
                    label="Linehaul manifest:"
                    value={props.manifest?.linehaul}
                    labelFixWidth
                    centered
                    optional
                  />
                  <LabeledValue
                    label="Delivery manifest:"
                    value={props.manifest?.delivery}
                    labelFixWidth
                    centered
                    optional
                  />
                  {
                    isOutForDelivery() &&
                    props.trailer &&
                    props.showOnMap &&
                    <TrailerLocation invoiceNumber={props.invoice?.invoiceNumber} />
                  }
                </>
              )}
              {!props.trailer &&
                !props.cheetahInfo?.driverName &&
                !props.cheetahInfo?.driverNumber &&
                !props.manifest?.linehaul &&
                !props.manifest?.delivery &&
                !isOutForDelivery() && <BlockPlaceholder />
              }
            </InfoBlock>
          )}
          <InfoBlock title="Appointment" className="xgs-appointment-info-block">
            {props.appointment ? (
              <>
                <LabeledValue
                  label="Date:"
                  value={props.appointment.date ? props.appointment.date.toUiDateFormat() : ''}
                  labelFixWidth
                />
                <LabeledValue
                  label="Time Window:"
                  value={props.appointment.earlyTime && props.appointment.lateTime 
                    ? `${props.appointment.earlyTime.toUiTimeFormat()} - ${props.appointment.lateTime.toUiTimeFormat()}`
                    : ''}
                  labelFixWidth
                />
                {props.appointment.confirmedDateTime && (
                  <LabeledValue
                    label="Confirmed:"
                    value={props.appointment.confirmedDateTime.toUiDateTimeFormat()}
                    labelFixWidth
                  />
                )}
                {props.appointment.confirmedUser && (
                  <LabeledValue
                    label="Confirmed by:"
                    value={props.appointment.confirmedUser}
                    labelFixWidth
                  />
                )}
                {props.appointment.notes && (
                  <div className="xgs-form__field__notes">
                    <strong>Notes:</strong>&nbsp;
                    {props.appointment.notes}
                  </div>
                )}
              </>
            ) : (
              <BlockPlaceholder />
            )}
          </InfoBlock>
        </>
      )}
    </InfoGrid>
  );
};

export default InvoiceDetailsInfo;
